/**
 * ArticleAudioPlayer
 * --------------------------------------------------------------------------
 * "Listen to this guide" button — reads an article/step aloud using the
 * browser's SpeechSynthesis API. Works entirely offline (no LLM call).
 *
 * - Auto-detects language from prop; falls back to en-US.
 * - Strips markdown so *stars* and → arrows aren't read literally.
 * - Play / Pause / Resume; auto-stops on unmount.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Volume2, Pause, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const stripForSpeech = (t) => (t || '')
  .replace(/```[\s\S]*?```/g, '')          // fenced code
  .replace(/`[^`]+`/g, '')                 // inline code
  .replace(/[*_#>]/g, '')                  // md syntax
  .replace(/\[[^\]]*\]\([^)]*\)/g, '')     // links
  .replace(/→/g, ', then ')
  .replace(/\s+/g, ' ')
  .trim();

export default function ArticleAudioPlayer({ text, lang = 'en', className = '' }) {
  const [state, setState] = useState('idle'); // idle | playing | paused
  const utterRef = useRef(null);

  useEffect(() => () => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }, []);

  const supported = typeof window !== 'undefined' && !!window.speechSynthesis;

  const play = () => {
    if (!supported) {
      toast.error('Your browser does not support read-aloud.');
      return;
    }
    if (state === 'paused') {
      window.speechSynthesis.resume();
      setState('playing');
      return;
    }
    window.speechSynthesis.cancel();
    const clean = stripForSpeech(text);
    if (!clean) return;
    const utter = new SpeechSynthesisUtterance(clean);
    utter.lang = lang === 'ar' ? 'ar-SA' : 'en-US';
    utter.rate = 0.95;
    utter.onend = () => setState('idle');
    utter.onerror = () => setState('idle');
    utterRef.current = utter;
    setState('playing');
    window.speechSynthesis.speak(utter);
  };

  const pause = () => {
    if (state === 'playing') {
      window.speechSynthesis.pause();
      setState('paused');
    }
  };

  const stop = () => {
    window.speechSynthesis.cancel();
    setState('idle');
  };

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      {state === 'idle' && (
        <Button
          type="button"
          onClick={play}
          size="sm"
          variant="ghost"
          className="text-sky-600 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-950/30 h-7 px-2"
          data-testid="article-audio-play"
        >
          <Volume2 className="w-3.5 h-3.5 mr-1.5" />
          <span className="text-xs">Listen to this guide</span>
        </Button>
      )}
      {state === 'playing' && (
        <>
          <Button
            type="button"
            onClick={pause}
            size="sm"
            variant="ghost"
            className="text-amber-600 dark:text-amber-400 h-7 px-2"
            data-testid="article-audio-pause"
          >
            <Pause className="w-3.5 h-3.5 mr-1.5" />
            <span className="text-xs">Pause</span>
          </Button>
          <Button
            type="button"
            onClick={stop}
            size="sm"
            variant="ghost"
            className="text-slate-500 h-7 px-2"
            data-testid="article-audio-stop"
          >
            <Square className="w-3.5 h-3.5" />
          </Button>
        </>
      )}
      {state === 'paused' && (
        <>
          <Button
            type="button"
            onClick={play}
            size="sm"
            variant="ghost"
            className="text-sky-600 dark:text-sky-400 h-7 px-2"
            data-testid="article-audio-resume"
          >
            <Volume2 className="w-3.5 h-3.5 mr-1.5" />
            <span className="text-xs">Resume</span>
          </Button>
          <Button
            type="button"
            onClick={stop}
            size="sm"
            variant="ghost"
            className="text-slate-500 h-7 px-2"
            data-testid="article-audio-stop"
          >
            <Square className="w-3.5 h-3.5" />
          </Button>
        </>
      )}
    </div>
  );
}
